import React from "react";
import { Heart, Microscope, Hand, Mountain } from "lucide-react";
import { useLang } from "../lib/i18n";

const aboutHero =
  "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB3ZWxsbmVzcyUyMG9mZmljZSUyMHRlYW0lMjB3b3Jrc2hvcHxlbnwwfHx8fDE3Nzg4NDI4MDZ8MA&ixlib=rb-4.1.0&q=85";
const gallery = [
  "https://images.unsplash.com/photo-1543248939-7b514293dc4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1730342754575-0c8ca8e62c0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1607835017779-c176b83b2dd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85",
];

const pillIcons = [Heart, Microscope, Hand, Mountain];

export default function About() {
  const { t } = useLang();

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="container-x pt-12 pb-16 md:pt-20 md:pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="label-eyebrow">{t.about.eyebrow}</span>
          <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink">
            {t.about.title}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-2xl">
            {t.about.lead}
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-float">
            <img src={aboutHero} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-y border-cream-300/70 bg-cream-200/40">
        <div className="container-x py-20 md:py-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.home.pillars.map((p, i) => {
            const Icon = pillIcons[i % pillIcons.length];
            return (
              <div
                key={p.t}
                data-testid={`about-pillar-${i}`}
                className="rounded-2xl p-7 bg-white border border-cream-300/70 hover:-translate-y-1 hover:shadow-float transition-all duration-500"
              >
                <Icon className="w-7 h-7 text-terracotta-400" strokeWidth={1.4} />
                <h3 className="mt-5 font-serif text-2xl text-ink">{p.t}</h3>
                <p className="mt-2 text-sm font-light text-ink/65 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* STORY */}
      <section className="container-x py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-serif italic text-2xl md:text-3xl text-ink/80 leading-snug">
              {t.about.story}
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            <div className="rounded-2xl p-7 bg-sage-100 border border-sage-200/60">
              <span className="label-eyebrow text-sage-700">{t.about.mission.title}</span>
              <p className="mt-4 font-serif text-2xl text-ink leading-snug">{t.about.mission.body}</p>
            </div>
            <div className="rounded-2xl p-7 bg-terracotta-50 border border-terracotta-100/60">
              <span className="label-eyebrow">{t.about.vision.title}</span>
              <p className="mt-4 font-serif text-2xl text-ink leading-snug">{t.about.vision.body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="container-x pb-24 md:pb-32">
        <div className="grid grid-cols-3 gap-3 md:gap-5">
          {gallery.map((src, i) => (
            <div
              key={i}
              className={`rounded-2xl overflow-hidden ${i === 1 ? "row-span-2 aspect-auto" : "aspect-[4/5]"}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
